import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { CloseIcon, SearchIcon } from '~/assets/icons';
import { formatCurrency } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import { IResponseEstimateItem } from '~/utils/interface/estimate';

type AddEstimateItemDialogProps = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  selectedEstimateItems: (estimateItems: IResponseEstimateItem[]) => void;
  editData?: Array<IResponseEstimateItem>;
  estimateItems: Array<IResponseEstimateItem>;
};

const AddEstimateItemDialog = (props: AddEstimateItemDialogProps) => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/invoice');

  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selectedEstimateItems, setSelectedEstimateItems] = useState<IResponseEstimateItem[]>([]);
  const [searchInput, setSearchInput] = useState<string>('');
  const [filteredEstimateItems, setFilteredEstimateItems] = useState<Array<IResponseEstimateItem>>([]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    setFilteredEstimateItems(props.estimateItems);
  }, [props.estimateItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, item: IResponseEstimateItem) => {
    const { value, checked } = event.target;
    if (checked) {
      setSelectedEstimateItems([...selectedEstimateItems, item]);
    } else {
      const newSelectedItems = selectedEstimateItems.filter((item) => Number(item.id) !== Number(value));
      setSelectedEstimateItems(newSelectedItems);
    }
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) setSelectedEstimateItems(filteredEstimateItems);
    else setSelectedEstimateItems([]);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchInput(e.target.value);
    setFilteredEstimateItems(
      props.estimateItems.filter(
        (item) =>
          (item.priceItemCode ?? '').toLowerCase().indexOf((e.target.value ?? '').toLowerCase()) > -1 ||
          (item.priceItemName ?? '').toLowerCase().indexOf((e.target.value ?? '').toLowerCase()) > -1
      )
    );
  };

  const handleCancel = () => {
    handleSetOpen(false);
  };
  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('estimateItems.title')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box className='flex justify-between gap-[10px]'>
          <Box className='w-1/3'>
            <TextField
              value={searchInput}
              onChange={(e) => handleSearchChange(e)}
              placeholder={t('estimateItems.searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox className='w-[20px] h-[20px]' color='primary' onChange={(event) => handleSelectAll(event)} />
            }
            label={t('selectAll', { ns: 'admin/common' })}
            value={true}
          />
        </Box>
        <Box className='flex flex-wrap'>
          {filteredEstimateItems.map((item, index) => (
            <Box className='relative w-1/2' key={index}>
              <IconButton className='absolute right-0 top-0'>
                <Checkbox
                  className='w-[20px] h-[20px]'
                  color='primary'
                  value={item.id}
                  onChange={(event) => handleCheckboxChange(event, item)}
                  checked={selectedEstimateItems.some((selectedItem) => Number(item.id) === Number(selectedItem.id))}
                />
              </IconButton>
              <Box className='block border-2 px-[50px] flex-1 m-1'>
                <Box className='flex mt-3 w-full'>
                  <InputLabel className='w-[100px] content-center'> {t('estimateItems.name')}</InputLabel>
                  <Typography>{item.priceItemName}</Typography>
                </Box>
                <Box className='flex mt-3 w-full'>
                  <InputLabel className='w-[100px] content-center'> {t('estimateItems.code')}</InputLabel>
                  <Typography>{item.priceItemCode}</Typography>
                </Box>
                <Box className='flex mt-3 w-full'>
                  <InputLabel className='w-[100px] content-center'> {t('estimateItems.unitPrice')}</InputLabel>
                  <Typography>{formatCurrency(item.unitPrice)}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            props.selectedEstimateItems(selectedEstimateItems);
            props.setOpen(false);
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddEstimateItemDialog);
